import { useLayoutEffect, useState, type RefObject } from "react";

export type FixedDropdownPlacement = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

export type FixedDropdownWidthMode = "match-anchor" | "fixed-end";

/**
 * Viewport-fixed coordinates for a dropdown below an anchor, updated on scroll (capture) and resize.
 * Avoids clipping when ancestors use overflow: auto|hidden (e.g. table scroll wrappers).
 */
export function useFixedDropdownPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  widthMode: FixedDropdownWidthMode,
  /** Used when widthMode is `fixed-end` (e.g. 200 for 12.5rem). */
  fixedWidthPx = 200,
): FixedDropdownPlacement | null {
  const [placement, setPlacement] = useState<FixedDropdownPlacement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }

    const anchor = anchorRef.current;
    if (!anchor) {
      setPlacement(null);
      return;
    }

    function update() {
      const el = anchorRef.current;
      if (!el) {
        return;
      }
      const r = el.getBoundingClientRect();
      const gap = 4;
      const pad = 8;
      let left: number;
      let width: number;

      if (widthMode === "match-anchor") {
        width = r.width;
        left = r.left;
      } else {
        width = fixedWidthPx;
        left = r.right - width;
      }

      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));

      const top = r.bottom + gap;
      const maxHeight = Math.max(120, window.innerHeight - top - pad);

      setPlacement({ top, left, width, maxHeight });
    }

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef, widthMode, fixedWidthPx]);

  return placement;
}
